import { useState, useRef, useEffect } from "react";
import {
  ShieldCheck,
  Zap,
  Coins,
  Wallet,
  RefreshCcw,
  Layers,
  Lock,
  Bot,
  ChevronDown,
  ShoppingCart,
  Megaphone,
  Shield,
  ExternalLink,
  PieChart,
} from "lucide-react";

const websiteFeatures = [
  {
    icon: <ShieldCheck className="w-8 h-8 text-purple-400" />,
    title: "Safety Burn",
    desc: "Burn any SPL tokens safely — if burned by mistake, we can refund them through our Telegram or Discord support.",
  },
  {
    icon: <Zap className="w-8 h-8 text-purple-400" />,
    title: "Gas-Free Transactions",
    desc: "No need to have any SOL in your wallet and risk losing it — we cover all transaction fees for you.",
  },
  {
    icon: <Wallet className="w-8 h-8 text-purple-400" />,
    title: "Multi-Wallet Support",
    desc: "Works with Phantom, Solflare, Bybit, OKX, Binance Wallet, Backpack & more.",
  },
  {
    icon: <Coins className="w-8 h-8 text-purple-400" />,
    title: "Lowest Fees",
    desc: "15% base, 7.5% for holders, down to 5.25% with our swap option — by matching competitors, fees go as low as 2.5%.",
  },
  {
    icon: <RefreshCcw className="w-8 h-8 text-purple-400" />,
    title: "Swap Boost",
    desc: "Toggle Swap ON to earn more — lower fees while investing into $RYS and boosting its ecosystem.",
  },
  {
    icon: <Layers className="w-8 h-8 text-purple-400" />,
    title: "Staking Revenue Sharing",
    desc: "50% of website revenue goes directly to staking rewards — earn passive income by holding $RYS.",
  },
];

const toolsUtilities = [
  {
    icon: <Lock className="w-8 h-8 text-purple-400" />,
    title: "Staking Platform",
    desc: "Stake and lock $RYS for 1–6 months to earn daily revenue-based rewards (18–120%+ APY).",
  },
];

const botFeatures = [
  {
    icon: <ShoppingCart className="w-5 h-5 text-purple-400" />,
    title: "Buy Bot",
    desc: "Real-time buy alerts for your token in Telegram groups.",
  },
  {
    icon: <Megaphone className="w-5 h-5 text-purple-400" />,
    title: "Raid Bot",
    desc: "Coordinate community raids and boost engagement.",
  },
  {
    icon: <Shield className="w-5 h-5 text-purple-400" />,
    title: "Guard Bot",
    desc: "Protect your groups from spam and scammers.",
  },
];

// Custom hook for intersection observer (iOS-safe)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

const SectionTitle = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true });
  
  return (
    <h4
      ref={ref}
      className={`text-xl font-semibold text-gray-300 mb-8 transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      style={{ 
        transitionDelay: `${delay}s`,
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      {children}
    </h4>
  );
};

const FeatureCard = ({ icon, title, desc, delay }) => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <div
      ref={ref}
      className={`group relative glass rounded-2xl p-6 flex flex-col items-center cursor-pointer overflow-hidden transition-all duration-500 transform-gpu hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        transitionDelay: isVisible ? '0s' : `${delay}s`,
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: isVisible ? 'translate3d(0,0,0)' : 'translate3d(0,40px,0)',
      }}
    >
      {/* Animated gradient border on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-[1px] rounded-2xl bg-[#0f0f18]" />
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/50 via-pink-500/50 to-purple-500/50" style={{ padding: '1px' }} />
      </div>
      
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/0 via-purple-600/20 to-purple-600/0 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 pointer-events-none" />
      
      {/* Icon with animated background */}
      <div className="relative z-10">
        <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative transition-transform duration-300 group-hover:scale-110">
          {icon}
        </div>
      </div>
      
      <h4 className="relative z-10 text-lg font-semibold text-white mt-4 mb-2 group-hover:text-purple-300 transition-colors duration-300">{title}</h4>
      <p className="relative z-10 text-gray-400 text-sm leading-relaxed text-center group-hover:text-gray-300 transition-colors duration-300">{desc}</p>
    </div>
  );
};

const PortfolioCard = () => {
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <a
      ref={ref}
      href="https://portfolio.refundyoursol.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`group relative glass rounded-2xl p-6 flex flex-col items-center cursor-pointer overflow-hidden transition-all duration-500 transform-gpu hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        transitionDelay: isVisible ? '0s' : '0.2s',
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: isVisible ? 'translate3d(0,0,0)' : 'translate3d(0,40px,0)',
      }}
    >
      {/* Animated gradient border on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-[1px] rounded-2xl bg-[#0f0f18]" />
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/50 via-pink-500/50 to-purple-500/50" style={{ padding: '1px' }} />
      </div>
      
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/0 via-purple-600/20 to-purple-600/0 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 pointer-events-none" />
      
      {/* Icon */}
      <div className="relative z-10">
        <div className="absolute inset-0 bg-purple-500/20 rounded-full blur-xl scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="relative transition-transform duration-300 group-hover:scale-110">
          <PieChart className="w-8 h-8 text-purple-400" />
        </div>
      </div>
      
      <h4 className="relative z-10 text-lg font-semibold text-white mt-4 mb-2 group-hover:text-purple-300 transition-colors duration-300 flex items-center gap-2">
        Portfolio Tracker
        <ExternalLink size={14} className="opacity-60 group-hover:opacity-100 transition-opacity" />
      </h4>
      <p className="relative z-10 text-gray-400 text-sm leading-relaxed text-center group-hover:text-gray-300 transition-colors duration-300">
        Track your Solana holdings, performance, and portfolio value across multiple wallets.
      </p>
    </a>
  );
};

const ExpandableBotCard = ({ delay }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);
  const isVisible = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <div
      ref={ref}
      className={`group relative glass rounded-2xl p-6 flex flex-col items-center overflow-hidden transition-all duration-500 transform-gpu hover:-translate-y-2 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ 
        transitionDelay: isVisible ? '0s' : `${delay}s`,
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: isVisible ? 'translate3d(0,0,0)' : 'translate3d(0,40px,0)',
      }}
    >
      {/* Animated gradient border on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
        <div className="absolute inset-[1px] rounded-2xl bg-[#0f0f18]" />
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500/50 via-pink-500/50 to-purple-500/50" style={{ padding: '1px' }} />
      </div>
      
      {/* Glow effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/0 via-purple-600/20 to-purple-600/0 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 pointer-events-none" />
      
      <div className="relative z-10 transition-transform duration-300 group-hover:scale-110">
        <Bot className="w-8 h-8 text-purple-400" />
      </div>
      <h4 className="relative z-10 text-lg font-semibold text-white mt-4 mb-2 group-hover:text-purple-300 transition-colors duration-300">RYS Bot Suite</h4>
      <p className="relative z-10 text-gray-400 text-sm leading-relaxed mb-4 text-center group-hover:text-gray-300 transition-colors duration-300">
        A powerful collection of Telegram bots to automate, protect, and grow your project.
      </p>
      
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="relative z-10 flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors text-sm font-medium"
      >
        {isExpanded ? "Show less" : "View all features"}
        <span
          className="transition-transform duration-300"
          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDown className="w-4 h-4" />
        </span>
      </button>

      {/* Expandable content */}
      <div
        className={`relative z-10 w-full overflow-hidden transition-all duration-300 ${
          isExpanded ? 'mt-4 max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        style={{ WebkitBackfaceVisibility: 'hidden' }}
      >
        <div className="grid gap-3 pt-4 border-t border-white/10">
          {botFeatures.map((bot, i) => (
            <div
              key={bot.title}
              className="flex items-start gap-3 text-left p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200"
              style={{
                transitionDelay: isExpanded ? `${i * 50}ms` : '0ms',
                opacity: isExpanded ? 1 : 0,
                transform: isExpanded ? 'translateX(0)' : 'translateX(-10px)',
              }}
            >
              <div className="mt-0.5">{bot.icon}</div>
              <div>
                <h5 className="text-sm font-semibold text-white">{bot.title}</h5>
                <p className="text-xs text-gray-400">{bot.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Utilities() {
  const headingRef = useRef(null);
  const headingVisible = useIsVisible(headingRef, { once: true });

  return (
    <section
      id="utilities"
      className="relative py-24 border-t border-white/5 bg-[#0b0b0f]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.1),transparent_70%)]" />
      <div className="relative max-w-7xl mx-auto px-6 text-center">
        <h3
          ref={headingRef}
          className={`text-3xl md:text-4xl font-bold mb-16 gradient-text transition-all duration-600 ${
            headingVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
          style={{ WebkitBackfaceVisibility: 'hidden' }}
        >
          Utilities & Features
        </h3>

        {/* Website Features */}
        <div className="mb-16">
          <SectionTitle delay={0}>Website Features</SectionTitle>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {websiteFeatures.map((u, i) => (
              <FeatureCard key={u.title} {...u} delay={i * 0.05} />
            ))}
          </div>
        </div>

        {/* Tools & Utilities */}
        <div className="mb-16">
          <SectionTitle delay={0.15}>Tools & Utilities</SectionTitle>
          <div className="grid gap-6 sm:grid-cols-2 max-w-2xl mx-auto">
            {toolsUtilities.map((u, i) => (
              <FeatureCard key={u.title} {...u} delay={0.15 + i * 0.05} />
            ))}
            <ExpandableBotCard delay={0.2} />
          </div>
        </div>

        {/* In Progress */}
        <div>
          <SectionTitle delay={0.2}>In Progress</SectionTitle>
          <div className="flex justify-center">
            <div className="w-full max-w-md">
              <PortfolioCard />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
